import { useEffect, useMemo, useRef, useState } from "react";
import { downloadZip } from "client-zip";
import {
  normalizeProfile,
  POSITION_OPTIONS,
  SLOT_TITLES,
  TEAM_OPTIONS,
  type PlayerProfile,
  type SlotState
} from "@vire-craque/shared";
import {
  API_BASE,
  assetUrl,
  createSession,
  deleteOrder,
  demoUnlock,
  exchangeFoto3x4Handoff,
  exchangeOrderAccess,
  getOrder,
  getSession,
  startPreview,
  sendDeliveryEmail,
  updateProfile,
  uploadPhoto,
  type SessionPayload
} from "./api";

type Stage = "landing" | "upload" | "generating" | "preview" | "unlocking" | "results";

const STORAGE_KEY = "vire-craque-session-v1";
const DEFAULT_PROFILE = normalizeProfile({});

function saveSession(value: { sessionId: string; csrfToken: string; orderId?: string }): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

function readSession(): { sessionId: string; csrfToken: string; orderId?: string } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as ReturnType<typeof readSession> : null;
  } catch {
    return null;
  }
}

function PhotoCard({
  slot,
  locked = false,
  revealed = true,
  watermarked = false,
  onReveal
}: {
  slot: SlotState;
  locked?: boolean;
  revealed?: boolean;
  watermarked?: boolean;
  onReveal?: () => void;
}) {
  const title = slot.title ?? SLOT_TITLES[slot.slot - 1];
  if (locked || !slot.assetId || slot.status !== "succeeded") {
    return (
      <article className={`photo-card locked ${slot.slot === 8 ? "glory" : ""}`}>
        <div className="lock-orbit"><span>✦</span></div>
        <strong>{title}</strong>
        <small>{slot.status === "failed" ? "Vamos tentar novamente" : "Foto surpresa"}</small>
      </article>
    );
  }
  return (
    <article className={`photo-card ${!revealed ? "ready-to-reveal" : ""} ${watermarked ? "watermarked" : ""}`}>
      <img src={assetUrl(slot.assetId)} alt={title} />
      {!revealed && (
        <button type="button" className="reveal-cover" onClick={onReveal}>
          <span>✦</span>
          <strong>REVELAR</strong>
          <small>{title}</small>
        </button>
      )}
      {revealed && <span className="photo-label">{title}</span>}
    </article>
  );
}

function ProgressRoute({ completed = 0 }: { completed?: number }) {
  const steps = ["Vestiário", "Túnel", "Campo", "Glória"];
  return (
    <div className="progress-route" aria-label="Progresso da geração">
      {steps.map((step, index) => (
        <div className={index < Math.ceil(completed / 2) ? "active" : ""} key={step}>
          <span>{index < Math.ceil(completed / 2) ? "✓" : index + 1}</span>
          <small>{step}</small>
        </div>
      ))}
    </div>
  );
}

export function App() {
  const [stage, setStage] = useState<Stage>("landing");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [profile, setProfile] = useState<PlayerProfile>(DEFAULT_PROFILE);
  const [customizing, setCustomizing] = useState(false);
  const [consented, setConsented] = useState(false);
  const [sessionId, setSessionId] = useState<string>();
  const [csrfToken, setCsrfToken] = useState<string>();
  const [orderId, setOrderId] = useState<string>();
  const [payload, setPayload] = useState<SessionPayload>();
  const [revealed, setRevealed] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [handoffAttached, setHandoffAttached] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const completed = payload?.slots.filter((slot) => slot.status === "succeeded").length ?? 0;
  const progress = Math.round((completed / (orderId ? 8 : 4)) * 100);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const access = params.get("access");
    const accessOrder = params.get("order");
    if (access && accessOrder) {
      void exchangeOrderAccess(accessOrder, access).then(() => {
        history.replaceState({}, "", location.pathname);
        setOrderId(accessOrder);
        setSessionId("email-access");
        setCsrfToken("");
        saveSession({ sessionId: "email-access", csrfToken: "", orderId: accessOrder });
        setStage("unlocking");
      }).catch((accessError) => {
        setError(accessError instanceof Error ? accessError.message : "Este link expirou.");
      });
      return;
    }
    const handoff = params.get("handoff");
    if (handoff) {
      void exchangeFoto3x4Handoff(handoff).then((created) => {
        history.replaceState({}, "", location.pathname);
        setSessionId(created.id);
        setCsrfToken(created.csrfToken);
        setPreviewUrl(assetUrl(created.inputAssetId));
        setHandoffAttached(true);
        setConsented(true);
        saveSession({ sessionId: created.id, csrfToken: created.csrfToken });
        setStage("upload");
      }).catch((handoffError) => {
        history.replaceState({}, "", location.pathname);
        setError(handoffError instanceof Error ? handoffError.message : "Não foi possível importar sua foto.");
      });
      return;
    }
    const saved = readSession();
    if (!saved) return;
    setSessionId(saved.sessionId);
    setCsrfToken(saved.csrfToken);
    setOrderId(saved.orderId);
    setStage(saved.orderId ? "unlocking" : "generating");
  }, []);

  useEffect(() => {
    if (!sessionId || !["generating", "preview", "unlocking", "results"].includes(stage)) return;
    let cancelled = false;
    const load = async () => {
      try {
        const next = orderId ? await getOrder(orderId) : await getSession(sessionId);
        if (cancelled) return;
        setPayload(next);
        setProfile(next.profile);
        if (orderId) {
          if (next.orderStatus === "ready") setStage("results");
          else setStage("unlocking");
        } else if (next.status === "preview_ready") {
          setStage("preview");
        } else if (next.status === "preview_failed") {
          setError("Não conseguimos concluir as fotos. Tente novamente mais tarde.");
        }
      } catch (loadError) {
        if (!cancelled) {
          localStorage.removeItem(STORAGE_KEY);
          setError(loadError instanceof Error ? loadError.message : "Não foi possível recuperar a sessão.");
          setStage("landing");
        }
      }
    };
    void load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [sessionId, orderId, stage]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const previewSlots = useMemo<SlotState[]>(() => {
    const bySlot = new Map(payload?.slots.map((slot) => [slot.slot, slot]));
    return [1, 2, 3, 4].map((slot) => bySlot.get(slot) ?? ({
      slot,
      kind: "preview" as const,
      status: "queued" as const,
      title: SLOT_TITLES[slot - 1] ?? `Foto ${slot}`
    }));
  }, [payload]);

  const allSlots = useMemo<SlotState[]>(() => {
    const bySlot = new Map(payload?.slots.map((slot) => [slot.slot, slot]));
    return Array.from({ length: 8 }, (_, index) => {
      const slot = index + 1;
      return bySlot.get(slot) ?? {
        slot,
        kind: slot <= 4 ? "preview" as const : "surprise" as const,
        status: "queued" as const,
        title: SLOT_TITLES[index] ?? `Foto ${slot}`
      };
    });
  }, [payload]);

  function chooseFile(next: File | undefined) {
    if (!next) return;
    if (!new Set(["image/jpeg", "image/png", "image/webp"]).has(next.type)) {
      setError("Escolha uma foto JPEG, PNG ou WebP.");
      return;
    }
    if (next.size > 10 * 1024 * 1024) {
      setError("A foto deve ter no máximo 10 MB.");
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(next);
    setPreviewUrl(URL.createObjectURL(next));
    setError(undefined);
  }

  async function generatePreview() {
    if ((!file && !handoffAttached) || !consented) return;
    setBusy(true);
    setError(undefined);
    try {
      const created = sessionId && csrfToken
        ? { id: sessionId, csrfToken }
        : await createSession();
      if (file) await uploadPhoto(created.id, created.csrfToken, file);
      await updateProfile(created.id, created.csrfToken, profile);
      await startPreview(created.id, created.csrfToken);
      setSessionId(created.id);
      setCsrfToken(created.csrfToken);
      saveSession({ sessionId: created.id, csrfToken: created.csrfToken });
      setStage("generating");
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Não foi possível iniciar.");
    } finally {
      setBusy(false);
    }
  }

  async function unlock() {
    if (!sessionId || !csrfToken) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await demoUnlock(sessionId, csrfToken);
      setOrderId(result.orderId);
      saveSession({ sessionId, csrfToken, orderId: result.orderId });
      setStage("unlocking");
    } catch (unlockError) {
      setError(unlockError instanceof Error ? unlockError.message : "Não foi possível continuar.");
    } finally {
      setBusy(false);
    }
  }

  async function downloadOne(slot: SlotState) {
    if (!slot.assetId) return;
    const response = await fetch(assetUrl(slot.assetId, true), { credentials: "include" });
    if (!response.ok) throw new Error("Falha ao baixar a foto.");
    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `vire-craque-${slot.slot}.jpg`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  async function downloadAll() {
    const ready = allSlots.filter((slot) => slot.assetId && slot.status === "succeeded");
    setDownloading(true);
    setError(undefined);
    try {
      const files = await Promise.all(ready.map(async (slot) => {
        const response = await fetch(assetUrl(slot.assetId!), { credentials: "include" });
        if (!response.ok) throw new Error(`Falha ao baixar a foto ${slot.slot}.`);
        return { name: `vire-craque-${slot.slot}.jpg`, input: await response.blob() };
      }));
      const blob = await downloadZip(files).blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "vire-craque-8-fotos.zip";
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : "Falha ao criar o ZIP.");
    } finally {
      setDownloading(false);
    }
  }

  async function sharePoster() {
    const hero = allSlots.find((slot) => slot.assetId && slot.status === "succeeded");
    if (!hero?.assetId) return;
    setSharing(true);
    setError(undefined);
    try {
      const response = await fetch(assetUrl(hero.assetId), { credentials: "include" });
      if (!response.ok) throw new Error("Não foi possível preparar o pôster.");
      const bitmap = await createImageBitmap(await response.blob());
      const canvas = document.createElement("canvas");
      canvas.width = 1080;
      canvas.height = 1350;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Seu navegador não suporta o pôster.");
      context.fillStyle = "#071426";
      context.fillRect(0, 0, canvas.width, canvas.height);
      const targetHeight = 1050;
      const scale = Math.max(canvas.width / bitmap.width, targetHeight / bitmap.height);
      const width = bitmap.width * scale;
      const height = bitmap.height * scale;
      context.drawImage(bitmap, (canvas.width - width) / 2, (targetHeight - height) / 2, width, height);
      const gradient = context.createLinearGradient(0, 760, 0, 1350);
      gradient.addColorStop(0, "rgba(7,20,38,0)");
      gradient.addColorStop(.42, "rgba(7,20,38,.92)");
      gradient.addColorStop(1, "#071426");
      context.fillStyle = gradient;
      context.fillRect(0, 600, 1080, 750);
      context.fillStyle = "#18d47b";
      context.font = "800 28px Inter, sans-serif";
      context.fillText("EU VIREI CRAQUE", 64, 1015);
      context.fillStyle = "#f7fafc";
      context.font = "900 92px Arial Narrow, sans-serif";
      context.fillText(profile.playerName, 64, 1118);
      context.fillStyle = "#f4c95d";
      context.font = "900 52px Inter, sans-serif";
      context.fillText(`#${profile.shirtNumber}  ·  ${profile.teamCode}`, 66, 1190);
      context.fillStyle = "#f7fafc";
      context.font = "800 32px Inter, sans-serif";
      context.fillText("VIRE CRAQUE", 64, 1280);
      context.fillStyle = "#94a8bb";
      context.font = "500 20px Inter, sans-serif";
      context.fillText("Imagem gerada por IA", 770, 1280);
      const poster = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Falha ao gerar pôster.")), "image/jpeg", .9)
      );
      const file = new File([poster], "eu-virei-craque.jpg", { type: "image/jpeg" });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title: "Eu virei craque!", text: "E você?", files: [file] });
      } else {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(poster);
        link.download = file.name;
        link.click();
        URL.revokeObjectURL(link.href);
      }
    } catch (shareError) {
      if (shareError instanceof DOMException && shareError.name === "AbortError") return;
      setError(shareError instanceof Error ? shareError.message : "Falha ao compartilhar.");
    } finally {
      setSharing(false);
    }
  }

  async function removeOrder() {
    if (!orderId || !csrfToken || !confirm("Excluir definitivamente todas as fotos?")) return;
    await deleteOrder(orderId, csrfToken);
    localStorage.removeItem(STORAGE_KEY);
    location.reload();
  }

  async function emailDelivery() {
    if (!orderId || !csrfToken || !email) return;
    setBusy(true);
    setError(undefined);
    try {
      await sendDeliveryEmail(orderId, csrfToken, email);
      setEmailSent(true);
    } catch (emailError) {
      setError(emailError instanceof Error ? emailError.message : "Falha ao enviar o e-mail.");
    } finally {
      setBusy(false);
    }
  }

  if (stage === "landing") {
    return (
      <main className="landing-shell">
        <header className="brand-bar"><span className="brand-mark">VC</span><b>VIRE CRAQUE</b><em>COPA 26</em></header>
        <section className="hero">
          <div className="hero-copy">
            <span className="eyebrow">SUA VEZ DE ENTRAR EM CAMPO</span>
            <h1>HOJE, O<br /><span>CRAQUE É VOCÊ.</span></h1>
            <p>Transforme uma selfie em <strong>8 fotos</strong> da sua jornada no maior palco do futebol.</p>
            <button className="primary-cta" onClick={() => setStage("upload")}>CRIAR MINHAS FOTOS <span>→</span></button>
            <small className="cta-proof">Veja 4 antes de pagar <i /> Sem assinatura</small>
            <div className="value-row"><span><b>8</b> fotos HD</span><span><b>4+4</b> surpresas</span><span><b>⚡</b> em minutos</span></div>
          </div>
          <div className="hero-visual" aria-label="Exemplo de ensaio de futebol">
            <div className="stadium-glow" />
            <div className="hero-card main"><div className="mock-player">10</div><span>A GLÓRIA</span></div>
            <div className="hero-card back"><div className="mock-player alt">✦</div><span>A ESTREIA</span></div>
            <div className="floating-score"><small>JOGADOR</small><b>VOCÊ</b><strong>10</strong></div>
          </div>
        </section>
        <footer className="landing-footer">Pagamento único · Imagens geradas por IA · Seus arquivos expiram automaticamente</footer>
      </main>
    );
  }

  if (stage === "upload") {
    return (
      <main className="app-shell">
        <header className="app-header"><button className="back" onClick={() => setStage("landing")}>←</button><b>VIRE CRAQUE</b><span>1 de 3</span></header>
        <section className="form-page">
          <div className="step-heading"><span className="eyebrow">PRIMEIRO PASSO</span><h2>Escolha sua melhor foto</h2><p>Rosto de frente, boa luz e sem outras pessoas.</p></div>
          <button className={`upload-zone ${previewUrl ? "has-photo" : ""}`} onClick={() => fileInput.current?.click()} type="button">
            {previewUrl ? <img src={previewUrl} alt="Sua foto selecionada" /> : <><span className="upload-icon">↑</span><b>ENVIAR UMA FOTO</b><small>JPEG, PNG ou WebP · até 10 MB</small></>}
            {previewUrl && <span className="change-photo">Trocar foto</span>}
          </button>
          <input ref={fileInput} hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => chooseFile(event.target.files?.[0])} />

          <button className="customize-toggle" type="button" onClick={() => setCustomizing((value) => !value)}>
            <span><b>Seu jogador</b><small>{profile.teamCode} · #{profile.shirtNumber} · {profile.position}</small></span><strong>{customizing ? "−" : "+"}</strong>
          </button>
          {customizing && (
            <div className="profile-panel">
              <label>Nome na camisa<input value={profile.playerName} maxLength={18} onChange={(e) => setProfile({ ...profile, playerName: e.target.value })} /></label>
              <label>Número<input type="number" min="0" max="99" value={profile.shirtNumber} onChange={(e) => setProfile({ ...profile, shirtNumber: Number(e.target.value) })} /></label>
              <label>Posição<select value={profile.position} onChange={(e) => setProfile({ ...profile, position: e.target.value as PlayerProfile["position"] })}>{POSITION_OPTIONS.map((item) => <option key={item.code} value={item.code}>{item.label}</option>)}</select></label>
              <label>Seleção<select value={profile.teamCode} onChange={(e) => setProfile({ ...profile, teamCode: e.target.value as PlayerProfile["teamCode"] })}>{TEAM_OPTIONS.map((item) => <option key={item.code} value={item.code}>{item.flag} {item.label}</option>)}</select></label>
              <label>Uniforme<select value={profile.kit} onChange={(e) => setProfile({ ...profile, kit: e.target.value as "home" | "away" })}><option value="home">Principal</option><option value="away">Visitante</option></select></label>
            </div>
          )}

          <label className="consent"><input type="checkbox" checked={consented} onChange={(e) => setConsented(e.target.checked)} /><span>Confirmo que esta foto é minha e autorizo seu uso para gerar este ensaio com IA.</span></label>
          {error && <div className="error-banner">{error}</div>}
          <button className="primary-cta full" disabled={(!file && !handoffAttached) || !consented || busy} onClick={() => void generatePreview()}>
            {busy ? "PREPARANDO..." : "VER MINHAS 4 FOTOS"}
          </button>
          <small className="free-note">Você ainda não paga nada</small>
        </section>
      </main>
    );
  }

  if (stage === "generating" || stage === "unlocking") {
    return (
      <main className="app-shell centered">
        <header className="app-header"><span /><b>VIRE CRAQUE</b><span>{stage === "generating" ? "2 de 3" : "Final"}</span></header>
        <section className="loading-page">
          <div className="loading-ball"><span>{progress}%</span></div>
          <span className="eyebrow">{stage === "generating" ? "SUA ESTREIA ESTÁ CHEGANDO" : "RUMO À GLÓRIA"}</span>
          <h2>{stage === "generating" ? "Preparando seu ensaio..." : "Criando suas surpresas..."}</h2>
          <p>{stage === "generating" ? "Acendendo as luzes do estádio e preparando seu uniforme." : `${completed} de 8 momentos já estão prontos.`}</p>
          <ProgressRoute completed={completed} />
          <div className="linear-progress"><span style={{ width: `${Math.max(8, progress)}%` }} /></div>
          <small>Pode levar alguns minutos. Não feche esta página.</small>
          {error && <div className="error-banner">{error}</div>}
        </section>
      </main>
    );
  }

  if (stage === "preview") {
    return (
      <main className="app-shell result-shell">
        <header className="app-header"><span /><b>VIRE CRAQUE</b><span>3 de 3</span></header>
        <section className="result-heading"><span className="success-pill">✓ SUAS FOTOS ESTÃO PRONTAS</span><h2>Você nasceu para<br />entrar em campo.</h2><p><b>4 de 8 fotos</b> prontas para ver</p></section>
        <section className="photo-grid">{previewSlots.map((slot) => <PhotoCard key={slot.slot} slot={slot} watermarked />)}</section>
        <section className="surprise-section"><span className="eyebrow gold">AINDA TEM MAIS</span><h3>4 momentos-surpresa</h3><p>Avance até a glória e revele o restante da sua jornada.</p><div className="photo-grid locked-grid">{[5, 6, 7, 8].map((slot) => <PhotoCard key={slot} locked slot={{ slot, kind: "surprise", status: "queued", title: SLOT_TITLES[slot - 1] ?? `Foto ${slot}` }} />)}</div></section>
        {error && <div className="error-banner page-error">{error}</div>}
        <div className="sticky-buy">
          <div><span>8 fotos em alta resolução</span><small>4 sem marca d'água + 4 surpresas</small></div>
          <button disabled={busy} onClick={() => void unlock()}>{busy ? "PREPARANDO..." : import.meta.env.VITE_DEMO_UNLOCK === "false" ? "PAGAMENTO EM BREVE" : "CONTINUAR PARA DEMO"}</button>
          <small>Pagamento será conectado na próxima etapa · Sem assinatura</small>
        </div>
      </main>
    );
  }

  return (
    <main className="app-shell result-shell final-results">
      <header className="app-header"><span /><b>VIRE CRAQUE</b><span>✓ Completo</span></header>
      <section className="result-heading"><span className="success-pill gold-bg">✦ JORNADA COMPLETA</span><h2>O palco é seu,<br /><span>{profile.playerName}.</span></h2><p>Toque nas cartas douradas para revelar suas surpresas.</p></section>
      <section className="photo-grid all-photos">{allSlots.map((slot) => <PhotoCard key={slot.slot} slot={slot} revealed={slot.slot <= 4 || revealed.includes(slot.slot)} onReveal={() => setRevealed((items) => [...items, slot.slot])} />)}</section>
      <section className="download-panel"><h3>Suas 8 fotos em alta resolução</h3><button className="primary-cta full" onClick={() => void downloadAll()} disabled={downloading}>{downloading ? "CRIANDO ZIP..." : "BAIXAR TODAS AS FOTOS ↓"}</button><button className="share-button" onClick={() => void sharePoster()} disabled={sharing}>{sharing ? "PREPARANDO PÔSTER..." : "COMPARTILHAR MEU PÔSTER ✦"}</button><div className="individual-downloads">{allSlots.map((slot) => <button key={slot.slot} onClick={() => void downloadOne(slot)} disabled={!slot.assetId}>Foto {slot.slot}</button>)}</div><small>Disponíveis por 7 dias</small>{csrfToken && <div className="email-delivery"><b>Receba também por e-mail</b>{emailSent ? <span>✓ Link enviado para {email}</span> : <div><input type="email" value={email} placeholder="voce@email.com" onChange={(event) => setEmail(event.target.value)} /><button onClick={() => void emailDelivery()} disabled={busy || !email}>ENVIAR</button></div>}</div>}</section>
      {csrfToken && <button className="delete-link" onClick={() => void removeOrder()}>Excluir minhas fotos</button>}
      {error && <div className="error-banner page-error">{error}</div>}
    </main>
  );
}
